import { Component } from "react";
import PageContext, { updatePageContext, getPageContext } from "../../components/Common/PageContext";
import { Container } from "@mui/material";
import {
	searchAsync,
	postReopenPurchaseOrderAsync,
	fetchReceivePurchaseOrderDetails,
	postReceiveAsync,
	getAsnBySsccAsync,
} from "../../services/apiHelper";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { setStateAsync } from "../../util/stateHelper";
import { getBasePathForReceiveType } from "../../util/receiveTypeMapping";
import {} from "../../services/apiHelper";
import { modalClosed } from "../../util/emptyObjectsUtil";
import { modals } from "../../util/modalUtil";
import { Modal } from "../../components/Modal/Modal";
import { updateInvoiceLineItems } from "../../util/lineItemUtil";
import { createOrUpdateReceive } from "../../util/receiveUtil";
import { v4 as uuidv4 } from "uuid";
import ArrivalsCheckinDialog from "../../components/Arrivals/ArrivalsCheckinDialog";
import { receiveStatus } from "../../util/receiveStatus";
import { snackBarAlerts } from "../../util/snackBarAlert";
import SnackbarAlert from "../../components/Snackbar/SnackbarAlert";
import { groupByPriorityStockFlag } from "../../util/priorityStockUtil";
import { isFeatureEnabledForLocation } from "../../util/featureUtil";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { SearchContainer } from "../../components/Search/components/SearchContainer";
import { searchHelperText } from "../../components/Search/constants";
import { PriorityStockDialog } from "../../components/PriorityStock/PriorityStockDialog";
import { AxiosResponse } from "axios";

interface SearchProps {
	classes?: any;
	graphProfile?: any;
	location?: any;
	history?: any;
}

interface SearchState {
	searchText: string;
	searchResults: any[];
	isShowLoadingDialog: boolean;
	errorMessage: string | null;
	isAlertSeverityError: boolean;
	showErrorAlert: boolean;
	openArrivalsCheckinDialog: boolean;
	purchaseOrders: any[];
	itemToCheckin: any;
	modal: any;
	isShowReopenPoLoadingDialog: boolean;
	isShowSubmittingArrivalLoading: boolean;
	snackBarAlert: any;
	scannedArrivalConsignmentNumber: string | null;
	openPriorityStockDialog: boolean;
	purchaseOrderPriorityStock: { poNumber: string | null; priorityStock: any };
	currentScannedBarcode: { value: string | null; isSscc: boolean; relatedAsn: any };
	isAsnLookupLoading: boolean;
	asnList: any[];
	isBlocking: boolean;
	showArrivalsCheckingDialogCloseConfirmation: boolean;
	error?: string;
	orders?: any[];
}

class Search extends Component<SearchProps, SearchState> {
	setStateAsync: (state: Partial<SearchState>) => Promise<void>;
	updatePageContext: (context: any, callback?: () => void) => void;
	getPageContext: () => any;
	initialSearchAttempted: boolean;

	constructor(props: SearchProps) {
		super(props);
		this.state = {
			searchText: "",
			searchResults: [],
			isShowLoadingDialog: false,
			errorMessage: null,
			isAlertSeverityError: false,
			showErrorAlert: false,
			openArrivalsCheckinDialog: false,
			purchaseOrders: [],
			itemToCheckin: null,
			modal: modalClosed,
			isShowReopenPoLoadingDialog: false,
			isShowSubmittingArrivalLoading: false,
			snackBarAlert: snackBarAlerts.closed,
			scannedArrivalConsignmentNumber: null,
			openPriorityStockDialog: false,
			purchaseOrderPriorityStock: { poNumber: null, priorityStock: {} },
			currentScannedBarcode: { value: null, isSscc: false, relatedAsn: null },
			isAsnLookupLoading: false,
			asnList: [],
			isBlocking: false,
			showArrivalsCheckingDialogCloseConfirmation: false,
		};
		this.setStateAsync = setStateAsync.bind(this);
		this.updatePageContext = updatePageContext.bind(this);
		this.getPageContext = getPageContext.bind(this);
		this.initialSearchAttempted = false;
	}

	static contextType = PageContext;

	async componentDidMount() {
		document.addEventListener("onScan", this.onScan);
		this.attemptInitialSearch();
	}

	attemptInitialSearch = async () => {
		if (this.initialSearchAttempted) return;

		this.initialSearchAttempted = true;
		const queryParams = new URLSearchParams(this.props.location.search);
		const searchQuery = queryParams.get("q");

		if (searchQuery) {
			console.log("Initial search with query:", searchQuery);
			await this.onSearchAsync(searchQuery);
		}
	};

	async componentWillUnmount() {
		document.removeEventListener("onScan", this.onScan);
	}

	onScan = async (e) => {
		const barcode = e.data.BarcodeValue;
		const { openArrivalsCheckinDialog } = this.state;

		// Check if the model is open (ConNote Scan)
		if (openArrivalsCheckinDialog) {
			await this.setStateAsync({ scannedArrivalConsignmentNumber: barcode });
			return;
		}
		// Trigger search when scanning barcode
		await this.onSearchAsync(barcode);
		// Then update the URL separately
		this.updateSearchUrl(barcode);
	};

	// Related to Receive by SSCC scan
	async tryGetAsnBySscc(ssccBarcode) {
		const { asnList } = this.state;

		//Search APP memory for ASN by SSCC. If found use it. Otherwise call Receiving API Get ASN By SSCC
		let foundAsn = asnList.find((a) => a.ssccList.find((c) => c === ssccBarcode));

		// ASN Not found in memory.
		if (!foundAsn) {
			// API Call to Receiving API Get ASN By SSCC. Then cache it in the APP
			foundAsn = await this.getAsnBySsccAsync(ssccBarcode);

			if (!foundAsn || !foundAsn.invoiceNumber || !foundAsn.purchaseOrderId) {
				// An ASN record with no Invoice Number or Purchase Order technically means this ASN has a problem.
				await this.setStateAsync({
					showErrorAlert: true,
					isAlertSeverityError: false,
					error: `No ASN found for scanned code ${ssccBarcode}`,
				});
				return null;
			}
		}
		if (!asnList.find((asn) => asn.asnId === foundAsn.asnId)) asnList.push(foundAsn);

		const currentScannedBarcode = { value: ssccBarcode, isSscc: true, relatedAsn: foundAsn };
		await this.setStateAsync({ currentScannedBarcode, asnList });
		return foundAsn;
	}

	// Related to Receive by SSCC scan
	getAsnBySsccAsync = async (barcode: string) => {
		try {
			await this.setStateAsync({ isAsnLookupLoading: true });
			const response: AxiosResponse = await getAsnBySsccAsync(barcode);
			return response.data;
		} catch (error) {
			await this.setStateAsync({
				isAlertSeverityError: true,
				isAsnLookupLoading: false,
				error: "Could not retrieve ASN by SSCC scan. Please try again and raise a support ticket if issue persists.",
				showErrorAlert: true,
			});
			return null;
		} finally {
			this.setStateAsync({ isAsnLookupLoading: false });
		}
	};

	onSearchAsync = async (value) => {
		try {
			let searchValue = value;

			let highlightInvoice = null; //To highlight the invoice found in ASN (when searching by SSCC)
			//Check if this is an SSCC barcode.
			if (value.startsWith("00") && value.length > 17) {
				if (this.isArrivalsFeatureEnabled()) {
					const asn = await this.tryGetAsnBySscc(value);
					if (!asn) {
						await this.setStateAsync({
							showErrorAlert: true,
							isAlertSeverityError: false,
							error: `No ASN found with SSCC ${value}.`,
						});
						return;
					}
					// use PO as search value
					searchValue = asn.purchaseOrderId;
					highlightInvoice = asn.invoiceNumber;
				} else {
					await this.setStateAsync({
						showErrorAlert: true,
						isAlertSeverityError: false,
						error: "Search by SSCC scan isn't available in your location yet.",
					});
				}
			}

			await this.setStateAsync({ isShowLoadingDialog: true });
			const { userLocation } = this.getPageContext();
			const response: AxiosResponse = await searchAsync(searchValue, userLocation);

			// This is used by Arrival Checkin Dialog for validation of consignment number
			const orders = (response.data as any[])
				.map((sr: any) => {
					if (sr.purchaseOrder && sr.purchaseOrder.purchaseOrderNumber) {
						const { invoices } = sr;
						const { purchaseOrderNumber } = sr.purchaseOrder;

						return {
							po: purchaseOrderNumber,
							invoices: invoices ? invoices.map((inv) => inv.invoiceNumber) : [],
						};
					} else if (sr.transfer && sr.transfer.sourceId) {
						return {
							sourceId: sr.transfer.sourceId,
						};
					}
					return null;
				})
				.filter((order) => order !== null);

			await this.setStateAsync({
				searchText: value,
				searchResults: (response.data as any[]).map((sr: any) => ({
					...sr,
					highlightInvoice: highlightInvoice,
				})),
				isShowLoadingDialog: false,
				orders: orders,
			});
		} catch (error) {
			await this.setStateAsync({
				isAlertSeverityError: true,
				searchResults: [],
				isShowLoadingDialog: false,
				error: "Could not retrieve search results. Please try again and raise a support ticket if issue persists. ",
				showErrorAlert: true,
				purchaseOrders: [],
			});
		}
	};

	onSearchResultSelectAsync = async (determinedReceiveType, purchaseOrderNumber, invoiceNumber, id) => {
		if (determinedReceiveType) {
			const basePath = getBasePathForReceiveType(determinedReceiveType);
			const queryParams = new URLSearchParams();
			if (invoiceNumber) queryParams.append("invoiceNumber", invoiceNumber);
			if (purchaseOrderNumber) queryParams.append("purchaseOrderNumber", purchaseOrderNumber);
			if (id) queryParams.append("id", id);
			queryParams.append("t", Date.now().toString());
			this.props.history.push(`${basePath}?${queryParams.toString()}`);
		} else {
			await this.setStateAsync({
				error: "Could not determine receive type. Please try again and raise a support ticket if issue persists.",
				showErrorAlert: true,
				isAlertSeverityError: false,
			});
		}
	};

	onDisabledSearchResultSelectAsync = async () => {
		await this.setStateAsync({
			error:
				"Sorry, this purchase order has multiple lines of the same SKU and unfortunately cannot be received through the app at this time. Please receive directly in Shopkeeper :(",
			showErrorAlert: true,
		});
	};

	handleCheckinDialogOpenAsync = async (poNumber, invNumber) => {
		const itemToCheckin = { purchaseOrderNumber: poNumber, invoiceNumber: invNumber };
		await this.setStateAsync({ openArrivalsCheckinDialog: true, itemToCheckin });
	};

	// Single Receive/Arrival Check-in
	handleCompleteCheckinAsync = async (arrival) => {
		try {
			await this.setStateAsync({ isShowSubmittingArrivalLoading: true });
			const { staffId, displayName } = this.props.graphProfile;
			arrival.createdByUserName = displayName;
			arrival.createdByUserId = staffId;
			const { itemToCheckin, searchResults } = this.state;
			const poNumber = itemToCheckin.purchaseOrderNumber;
			const invNumber = itemToCheckin.invoiceNumber;
			const receiveDetailsResponse = await fetchReceivePurchaseOrderDetails(poNumber, invNumber, null);
			const receiveDetails = {
				Invoice: receiveDetailsResponse.data.invoice,
				PurchaseOrder: receiveDetailsResponse.data.purchaseOrder,
				Products: receiveDetailsResponse.data.products,
				ProductPrices: receiveDetailsResponse.data.productPrices,
				CustomerOrders: receiveDetailsResponse.data.customerOrders,
				PriorityStock: receiveDetailsResponse.data.priorityStock,
			};
			const { productGroups } = this.getPageContext();

			const updatedInvoiceLineItems = updateInvoiceLineItems(
				receiveDetails.Invoice.lineItems,
				receiveDetails.Products,
				productGroups,
				receiveDetails.ProductPrices,
				receiveDetails.PurchaseOrder,
				receiveDetails.CustomerOrders.inventory
			);
			const receiveDate = arrival.dateArrived;
			const receiveId = uuidv4();
			const receive = createOrUpdateReceive(
				receiveDetails,
				updatedInvoiceLineItems,
				this.props.graphProfile,
				receiveDate,
				receiveId,
				null
			);
			receive.arrival = arrival;
			receive.status = receiveStatus.arrived;
			const result = await postReceiveAsync(receive);
			await this.setStateAsync({
				isShowSubmittingArrivalLoading: false,
				snackBarAlert: snackBarAlerts.success.savingArrival,
			});
			await this.handleCheckinDialogCloseAsync(false);

			if (result) {
				const po = searchResults.find((sr) => sr.purchaseOrder.purchaseOrderNumber === poNumber);
				const invoice = po?.invoices.find((inv) => inv.invoiceNumber === invNumber);
				if (invoice) invoice.status = receiveStatus.arrived;

				await this.showPriorityStock(receiveDetails.PriorityStock, poNumber, updatedInvoiceLineItems);
			}
		} catch (e) {
			await this.setStateAsync({ snackBarAlert: snackBarAlerts.error.savingArrival });
			console.log(e); // TODO: Proper error handling log to appInsights
		} finally {
			await this.setStateAsync({ isShowSubmittingArrivalLoading: false });
			await this.handleCheckinDialogCloseAsync(false);
		}
	};

	async showPriorityStock(priorityStock, poNumber, lineItems) {
		if (priorityStock && priorityStock.length > 0) {
			const groupedPriorityStock = groupByPriorityStockFlag(priorityStock, lineItems);
			const poPriorityStock = { priorityStock: groupedPriorityStock, poNumber };
			await this.setStateAsync({ openPriorityStockDialog: true, purchaseOrderPriorityStock: poPriorityStock });
		}
	}

	// Arrivals: This is a common close handler for both dialogs. The Normal check-in dialog and SSCC scan check-in dialog
	handleCheckinDialogCloseAsync = async (requiresConfirmation) => {
		await this.setStateAsync({
			showArrivalsCheckingDialogCloseConfirmation: requiresConfirmation,
			isBlocking: requiresConfirmation,
		});
		if (!requiresConfirmation) this.handleCloseConfirmationAsync(true);
	};

	handleCloseConfirmationAsync = async (confirmed) => {
		await this.setStateAsync({
			showArrivalsCheckingDialogCloseConfirmation: false,
			isBlocking: false,
		});
		if (confirmed) {
			await this.setStateAsync({
				openArrivalsCheckinDialog: false,
				scannedArrivalConsignmentNumber: null,
				itemToCheckin: null,
				asnList: [],
			});
			return true;
		}
		return false;
	};

	handleCloseAlertAsync = async (event?: any, reason?: string) => {
		if (reason === "clickaway") return;

		await this.setStateAsync({ showErrorAlert: false, error: null, isAlertSeverityError: false });
	};

	onCloseModal = async () => {
		await this.setStateAsync({ modal: modalClosed });
	};

	onReopenPoClick = async (purchaseOrderNumber) => {
		try {
			await this.setStateAsync({ isShowReopenPoLoadingDialog: true });
			await postReopenPurchaseOrderAsync(purchaseOrderNumber);
			await this.setStateAsync({ isShowReopenPoLoadingDialog: false });
			await this.setStateAsync({
				modal: {
					...modals.reopenPurchaseOrderSuccess,
					primaryButtonName: "Ok",
					onPrimaryButtonClick: this.onCloseModal,
				},
			});
		} catch (e) {
			await this.setStateAsync({ isShowReopenPoLoadingDialog: false });
			await this.setStateAsync({
				modal: { ...modals.reopenPurchaseOrderError, onPrimaryButtonClick: this.onCloseModal },
			});
		}
	};

	handleCloseSnackbarAlertAsync = async () => {
		await this.setStateAsync({ snackBarAlert: snackBarAlerts.closed });
	};

	handlePriorityStockDialogCloseAsync = async () => {
		await this.setStateAsync({ openPriorityStockDialog: false });
	};

	isArrivalsFeatureEnabled = () => {
		const {
			userLocation,
			featureConfiguration: { arrivals },
		} = this.getPageContext();

		return isFeatureEnabledForLocation(userLocation, arrivals);
	};

	isTransfersFeatureEnabled = () => {
		const {
			userLocation,
			featureConfiguration: { transfers },
		} = this.getPageContext();

		return isFeatureEnabledForLocation(userLocation, transfers);
	};

	updateSearchUrl = (searchValue) => {
		if (searchValue) {
			const searchParams = new URLSearchParams();
			searchParams.append("q", searchValue);
			const { userLocation } = this.getPageContext();

			// Use window.history directly to avoid React Router re-renders
			window.history.pushState({}, "", `?${searchParams.toString()}&locationId=${userLocation.toString()}`);
		}
	};

	componentDidUpdate(prevProps) {
		if (this.props.location.search !== prevProps.location.search) {
			const queryParams = new URLSearchParams(this.props.location.search);
			const searchQuery = queryParams.get("q");
			if (searchQuery) {
				this.onSearchAsync(searchQuery);
			}
		}
	}

	render() {
		const { userLocation } = this.getPageContext();
		const isTransfersFeatureEnabled = this.isTransfersFeatureEnabled();
		const helperText = isTransfersFeatureEnabled ? searchHelperText.transfer : searchHelperText.general;
		const { searchText, searchResults } = this.state;

		const handleSearch = async (value) => {
			await this.onSearchAsync(value);
			// Only update URL here after search completes
			await this.updateSearchUrl(value);
		};
		return (
			<Container disableGutters maxWidth="md">
				<SnackbarAlert
					open={this.state.snackBarAlert.isOpen}
					message={this.state.snackBarAlert.message}
					severity={this.state.snackBarAlert.severity}
					onClose={this.handleCloseSnackbarAlertAsync}
				/>
				<SearchContainer
					helperText={helperText}
					searchText={searchText}
					searchResults={searchResults}
					userLocation={userLocation}
					handleSearch={handleSearch}
					onSearchResultSelectAsync={this.onSearchResultSelectAsync}
					onDisabledSearchResultSelectAsync={this.onDisabledSearchResultSelectAsync}
					handleCheckinDialogOpenAsync={this.handleCheckinDialogOpenAsync}
					onReopenPoClick={this.onReopenPoClick}
				/>
				{this.state.isShowLoadingDialog && (
					<LoadingDialog message="Retrieving results..." isOpen={this.state.isShowLoadingDialog} />
				)}
				{this.state.showErrorAlert && (
					<ModalWithActionButton
						title={this.state.isAlertSeverityError ? "Error" : "Warning"}
						alertType={this.state.isAlertSeverityError ? "error" : "warning"}
						primaryButtonName="Ok"
						onPrimaryButtonClick={() => this.handleCloseAlertAsync()}
						isOpen={this.state.showErrorAlert}
						message={this.state.error}
					/>
				)}
				{this.state.modal.isOpen && <Modal {...this.state.modal} onSecondaryButtonClick={this.onCloseModal} />}
				{this.state.isShowReopenPoLoadingDialog && (
					<LoadingDialog message={"Loading..."} isOpen={this.state.isShowReopenPoLoadingDialog} />
				)}
				{this.state.isShowSubmittingArrivalLoading && (
					<LoadingDialog message="Submitting arrival..." isOpen={this.state.isShowSubmittingArrivalLoading} />
				)}
				{this.state.isAsnLookupLoading && (
					<LoadingDialog message={"Loading..."} isOpen={this.state.isAsnLookupLoading} />
				)}
				<ArrivalsCheckinDialog
					purchaseOrders={this.state.purchaseOrders}
					isOpen={this.state.openArrivalsCheckinDialog}
					onClose={this.handleCheckinDialogCloseAsync}
					onCompleteCheckin={this.handleCompleteCheckinAsync}
					scannedConsignmentNumber={this.state.scannedArrivalConsignmentNumber}
					isBlocking={this.state.isBlocking}
				/>
				<PriorityStockDialog
					isOpen={this.state.openPriorityStockDialog}
					onClose={this.handlePriorityStockDialogCloseAsync}
					purchaseOrderPriorityStock={this.state.purchaseOrderPriorityStock}
				/>
				{this.state.showArrivalsCheckingDialogCloseConfirmation && (
					<ModalWithActionButton
						title="Warning"
						alertType="warning"
						primaryButtonName="Yes"
						secondaryButtonName="No"
						onPrimaryButtonClick={() => this.handleCloseConfirmationAsync(true)}
						onSecondaryButtonClick={() => this.handleCloseConfirmationAsync(false)}
						isOpen={this.state.showArrivalsCheckingDialogCloseConfirmation}
						message="Are you sure you want to exit?"
					/>
				)}
			</Container>
		);
	}
}
export default Search;
